import type { Address } from '@hoodmorning/contracts';
import { encodePacked, keccak256, toBytes } from 'viem';

const NONCE_KEY_BITS = 152n;
const NONCE_KEY_MASK = (1n << NONCE_KEY_BITS) - 1n;
const NONCE_NAMESPACE = keccak256(toBytes('hoodmorning.product1.nonce-lane.v1'));

export function nonceLaneFor(vault: Address, idempotencyKey: string): bigint {
  const normalizedKey = idempotencyKey.trim();
  if (normalizedKey.length === 0) throw new Error('idempotencyKey must not be empty');

  const hash = keccak256(
    encodePacked(
      ['bytes32', 'address', 'string'],
      [NONCE_NAMESPACE, vault, normalizedKey],
    ),
  );
  const lane = BigInt(hash) & NONCE_KEY_MASK;
  return lane === 0n ? 1n : lane;
}

export function isValidNonceLane(value: bigint): boolean {
  return value > 0n && value <= NONCE_KEY_MASK;
}

/** Alchemy MAv2 5.2.0 / v2.0.0 SMA fallback entity 0, global validation flag 1.
 * EntryPoint stores the uint192 key above its low uint64 sequence counter.
 */
export function smaBOwnerNonceKey(lane: bigint): bigint {
  if (!isValidNonceLane(lane)) throw new RangeError('Invalid owner nonce lane');
  return (lane << 40n) | 1n;
}

export function assertSmaBOwnerNonce(nonce: bigint, lane: bigint): void {
  if (nonce < 0n || nonce >= 1n << 256n || nonce >> 64n !== smaBOwnerNonceKey(lane)) {
    throw new Error('Nonce does not belong to the requested fallback-owner lane');
  }
}

export class NonceLaneAllocator {
  readonly #assigned = new Map<string, bigint>();
  readonly #ownersByLane = new Map<string, string>();

  allocate(vault: Address, idempotencyKey: string): bigint {
    const stableKey = `${vault.toLowerCase()}:${idempotencyKey.trim()}`;
    const prior = this.#assigned.get(stableKey);
    if (prior !== undefined) return prior;

    const lane = nonceLaneFor(vault, idempotencyKey);
    const laneKey = `${vault.toLowerCase()}:${lane}`;
    const priorOwner = this.#ownersByLane.get(laneKey);
    if (priorOwner !== undefined && priorOwner !== stableKey) {
      throw new Error('Nonce-lane collision; allocate a fresh operation id');
    }

    this.#assigned.set(stableKey, lane);
    this.#ownersByLane.set(laneKey, stableKey);
    return lane;
  }
}
